import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef, useState } from 'react';
import { Send, Mail, MapPin } from 'lucide-react';
import { toast } from 'sonner';

const projectTypes = [
  'Video Production',
  'Social Media Management',
  'Brand Identity',
  'Website Development',
  'App Development',
  'AI Automation',
  'Full Package',
];

const budgetRanges = [
  'Under $500',
  '$500 - $1,000',
  '$1,000 - $2,500',
  '$2,500 - $5,000',
  '$5,000+',
];

export default function Contact() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    project_type: '',
    budget_range: '',
    message: '',
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (
      !formData.name ||
      !formData.email ||
      !formData.project_type ||
      !formData.budget_range ||
      !formData.message
    ) {
      toast.error('Por favor completa todos los campos requeridos');
      return;
    }

    setIsSubmitting(true);

    try {
      const payload = new FormData();
      payload.append('name', formData.name);
      payload.append('email', formData.email);
      payload.append('company', formData.company);
      payload.append('project_type', formData.project_type);
      payload.append('budget_range', formData.budget_range);
      payload.append('message', formData.message);
      payload.append('_subject', 'Nuevo mensaje desde OrianPartners');

      const response = await fetch('https://formspree.io/f/xjgaywnp', {
        method: 'POST',
        body: payload,
        headers: {
          Accept: 'application/json',
        },
      });

      const result = await response.json();

      if (response.ok) {
        toast.success('¡Mensaje enviado! Nos pondremos en contacto pronto.');
        setFormData({
          name: '',
          email: '',
          company: '',
          project_type: '',
          budget_range: '',
          message: '',
        });
      } else {
        toast.error(result?.errors?.[0]?.message || 'Error al enviar el mensaje');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Algo salió mal. Por favor intenta de nuevo.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      ref={ref}
      className="relative py-24 md:py-32 bg-black"
      data-testid="contact-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-24">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
          >
            <span className="text-xs font-mono tracking-[0.3em] uppercase text-[#D4AF37] mb-4 block">
              Get in Touch
            </span>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-white mb-6">
              Inicia Tu
              <br />
              <span className="gold-text">Proyecto</span>
            </h2>
            <p className="text-base md:text-lg text-zinc-400 leading-relaxed mb-12 max-w-md">
              Construyamos algo poderoso juntos. Cuéntanos sobre tu visión
              y crearemos una estrategia para hacerla realidad.
            </p>

            <div className="space-y-6">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={isInView ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.5, delay: 0.3 }}
                className="flex items-center gap-4"
              >
                <div className="w-12 h-12 border border-white/10 flex items-center justify-center">
                  <Mail size={20} className="text-[#D4AF37]" />
                </div>
                <div>
                  <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Email</p>
                  <a
                    href="mailto:orianpartners07@gmail.com"
                    className="text-white hover:text-[#D4AF37] transition-colors"
                  >
                    orianpartners07@gmail.com
                  </a>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={isInView ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.5, delay: 0.4 }}
                className="flex items-center gap-4"
              >
                <div className="w-12 h-12 border border-white/10 flex items-center justify-center">
                  <MapPin size={20} className="text-[#D4AF37]" />
                </div>
                <div>
                  <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Location</p>
                  <p className="text-white">Santo Domingo, RD / Remote</p>
                </div>
              </motion.div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <form onSubmit={handleSubmit} className="space-y-8" data-testid="contact-form">
              <div>
                <input
                  type="text"
                  name="name"
                  placeholder="Tu Nombre *"
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full bg-transparent border-b border-white/20 focus:border-[#D4AF37] focus:outline-none px-0 py-4 text-white placeholder:text-zinc-600 transition-colors"
                  data-testid="contact-name-input"
                />
              </div>

              <div>
                <input
                  type="email"
                  name="email"
                  placeholder="Tu Correo *"
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full bg-transparent border-b border-white/20 focus:border-[#D4AF37] focus:outline-none px-0 py-4 text-white placeholder:text-zinc-600 transition-colors"
                  data-testid="contact-email-input"
                />
              </div>

              <div>
                <input
                  type="text"
                  name="company"
                  placeholder="Nombre de Empresa"
                  value={formData.company}
                  onChange={handleChange}
                  className="w-full bg-transparent border-b border-white/20 focus:border-[#D4AF37] focus:outline-none px-0 py-4 text-white placeholder:text-zinc-600 transition-colors"
                  data-testid="contact-company-input"
                />
              </div>

              <div>
                <select
                  name="project_type"
                  value={formData.project_type}
                  onChange={handleChange}
                  className="w-full bg-transparent border-b border-white/20 focus:border-[#D4AF37] focus:outline-none px-0 py-4 text-white appearance-none cursor-pointer transition-colors [&>option]:bg-zinc-950 [&>option]:text-white"
                  data-testid="contact-project-type-select"
                >
                  <option value="" disabled className="text-zinc-600">
                    Tipo de Proyecto *
                  </option>
                  {projectTypes.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <select
                  name="budget_range"
                  value={formData.budget_range}
                  onChange={handleChange}
                  className="w-full bg-transparent border-b border-white/20 focus:border-[#D4AF37] focus:outline-none px-0 py-4 text-white appearance-none cursor-pointer transition-colors [&>option]:bg-zinc-950 [&>option]:text-white"
                  data-testid="contact-budget-select"
                >
                  <option value="" disabled className="text-zinc-600">
                    Rango de Presupuesto *
                  </option>
                  {budgetRanges.map((range) => (
                    <option key={range} value={range}>
                      {range}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <textarea
                  name="message"
                  placeholder="Cuéntanos sobre tu proyecto *"
                  value={formData.message}
                  onChange={handleChange}
                  rows={4}
                  className="w-full bg-transparent border-b border-white/20 focus:border-[#D4AF37] focus:outline-none px-0 py-4 text-white placeholder:text-zinc-600 resize-none transition-colors"
                  data-testid="contact-message-textarea"
                />
              </div>

              <motion.button
                type="submit"
                disabled={isSubmitting}
                className="group w-full flex items-center justify-center gap-3 bg-[#D4AF37] text-black hover:bg-white px-8 py-5 text-xs font-bold tracking-[0.15em] uppercase transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                whileHover={{ scale: isSubmitting ? 1 : 1.01 }}
                whileTap={{ scale: isSubmitting ? 1 : 0.99 }}
                data-testid="contact-submit-btn"
              >
                {isSubmitting ? (
                  'Enviando...'
                ) : (
                  <>
                    Enviar Mensaje
                    <Send size={16} className="group-hover:translate-x-1 transition-transform" />
                  </>
                )}
              </motion.button>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
